<template>
  <form class="tw:!mx-auto tw:!max-w-3xl" @submit.prevent="submit()">
    <div class="od-card">
      <div class="od-title">Fill your information</div>

      <div v-for="pax of passengers" class="tw:!flex tw:!flex-col tw:md:!flex-row tw:!mb-5 tw:md:!mb-0 tw:!space-x-3">
        <div class="tw:!flex tw:!space-x-3 tw:!w-full">
          <fieldset>
            <legend>Prefix</legend>

            <select v-model="pax.prefix" class="od-input">
              <option v-for="prefix of ['MR', 'MS', 'MRS']" :value="prefix">{{ prefix }}</option>
            </select>
          </fieldset>
  
          <fieldset class="tw:!w-full">
            <legend>First Name</legend>
            <input
              class="od-input tw:w-full"
              v-model="pax.first_name" 
              placeholder="Your given name"
              minlength="1"
              maxlength="50"
              pattern="^[A-Za-z]+(?: [A-Za-z]+)*$"
              required
              @keyup="pax.first_name = pax.first_name.toUpperCase()"
            />
          </fieldset>
        </div>

        <fieldset class="tw:w-full">
          <legend>Family Name</legend>
          <input
              class="od-input tw:w-full"
              v-model="pax.family_name"
              placeholder="Your last name"
              minlength="1"
              maxlength="50"
              pattern="^[A-Za-z]+(?: [A-Za-z]+)*$"
              required
              @keyup="pax.family_name = pax.family_name.toUpperCase()"
            />
        </fieldset>
      </div>

      <p class="tw:!text-sm tw:!text-orange-400">
        Notice: Passenger name contains character from <code>a</code> to <code>Z</code> only
      </p>

      <div class="tw:!border-t tw:!border-stone-200 tw:!mx-2 tw:!my-3" />

      <div class="tw:!flex tw:!flex-col tw:md:!flex-row tw:!space-x-3">
        <div class="tw:!flex tw:!space-x-3 tw:!w-full">
          <fieldset style="display: none;">
            <legend>Prefix</legend>

            <select v-model="contact.prefix" class="od-input">
              <option value="" disabled>Pick One</option>
              <option v-for="prefix of regions" :value="prefix">{{ prefix }}</option>
            </select>
          </fieldset>

          <fieldset class="tw:w-full">
            <legend>Phone number</legend>
            <input class="od-input tw:w-full" v-model="contact.phone" placeholder="Enter phone number" pattern="^[0-9]+$" minlength="1" maxlength="20" required />
            <Description>Enter number only</Description>
          </fieldset>
        </div>

        <fieldset class="tw:w-full">
          <legend>Email</legend>
          <input
            class="od-input tw:w-full"
            v-model="contact.email"
            placeholder="Your email address"
            type="email"
            required
            @keyup="contact.email = contact.email.toLowerCase()"
          />
          <Description>We'll send onward ticket to this email address</Description>
        </fieldset>
      </div>

      <div class="tw:!p-3 tw:!border tw:!border-stone-300 tw:!rounded-lg tw:!mt-3">
        <Checkbox v-model="isExtendedPlan">
          <template #label>
            <div class="tw:!leading-none">I want 7 days valid reservation</div>
            <div class="tw:!text-sm tw:!opacity-70 tw:!mt-1">
              + $7 per way per passenger, money-back guarantee
            </div>
          </template>
        </Checkbox>
      </div>

      <div class="tw:!flex tw:!justify-between tw:!mt-5">
        <button class="od-button-outline" type="button" @click="emit('back')">Back</button>
        <button class="od-button" type="submit" :disabled="props.loading">
          {{ props.loading ? 'Processing...' : 'Continue' }}
        </button>
      </div>
    </div>
  </form>
</template>

<script lang="ts" setup>
import countries from '~/resources/countries.json'
import Checkbox from './ui/Checkbox.vue'
import { h, reactive, watch, ref, computed } from 'vue'
import { ipGeo } from '~/utils/geo'

const regions = new Set(countries.map(i => i.prefix).sort((a, b) => +a - +b))

const Description = h('span', { class: `tw:!text-sm tw:!ml-1 tw:!opacity-80` })

const props = withDefaults(defineProps<{
  pax?: number,
  loading?: boolean,
  duration?: '7days'
}>(), {
  pax: 1
})

export type Passenger = {
  prefix: 'MR'|'MS'|'MRS',
  family_name: string
  first_name: string
}

const duration = ref<'7days'|undefined>(props.duration)

const isExtendedPlan = computed({
  get: () => duration.value === '7days',
  set: (check) => duration.value = check ? '7days' : undefined
})

const passengers = reactive(new Array<Passenger>())

const contact = reactive({
  phone: '',
  email: '',
  prefix: ''
})

const region = ipGeo().then(data => {
  if (! data) return '+84'

  const prefix = !data
    ? '+84'
    : countries.find(item => item.iata === data.country)?.prefix || '+84'

  contact.prefix = prefix

  return prefix
})

const emit = defineEmits<{
  submit: [passengers: Passenger[], contact: typeof contact, duration?: '7days'],
  back: []
}>()

const allocate = () => {
  const total = props.pax

  if (passengers.length > total) {
    return passengers.length = total
  }

  for (let i = passengers.length - 1; i < total; i++) {
    passengers[i] = { prefix: 'MR', family_name: '', first_name: '' }
  }
}

const submit = async () => {
  const prefix = await region.catch(() => '+84')


  emit('submit', passengers, { ...contact, prefix }, duration.value)
}

watch(() => props.pax, allocate, { immediate: true })
watch(() => props.duration, val => duration.value = val) 
</script>